import * as v from 'valibot';

import { isNumericIdString, reportThrottled } from '#shared';

import type { DomainEventFeed, DomainEventFrame } from '../model/types';

const REPORT_THROTTLE_MS = 60_000;

// bigint PK / seq carried as a decimal string — shares the one numeric-id
// predicate with the seq cursor (domainEventCursor) so the check can't drift.
const numericString = v.pipe(
  v.string(),
  v.check(isNumericIdString, 'must be a numeric id string'),
);

// Must be a timestamp the adapter can parse — occurredAt feeds
// `offsetMs = Date.parse(occurredAt) - t0Ms`, and a NaN offset would silently
// poison every offset-based sort and the alert stepper. Validating with the same
// `Date.parse` the adapter uses drops truly-malformed frames at the boundary
// (matching parseDomainEventFeed's drop-malformed policy) without false-dropping
// any ISO 8601 format Date.parse accepts.
const parseableTimestamp = v.pipe(
  v.string(),
  v.check(
    value => !Number.isNaN(Date.parse(value)),
    'occurredAt must be a parseable timestamp',
  ),
);

// A string that also accepts null on the wire, normalized to undefined so the
// parsed frame matches `DomainEventFrame`'s `?: string` (never `| null`).
const nullableString = v.pipe(
  v.nullable(v.string()),
  v.transform(value => value ?? undefined),
);

export const domainEventFrameSchema = v.object({
  kind: v.literal('domainEvent'),
  id: numericString,
  type: v.pipe(v.string(), v.nonEmpty()),
  category: v.picklist(['scheduled', 'actual']),
  domain: v.string(),
  severity: v.string(),
  entityId: v.pipe(v.string(), v.nonEmpty()),
  // Case identity (TMDXNC-37). MUST be declared: `v.object` STRIPS undeclared
  // keys, so omitting them here would drop both fields at this boundary even
  // once the BE sends them. (The original bug was upstream of this — the BE
  // serializer never emitted them — but this schema is the second gate.)
  //
  // `null` is tolerated and normalized to undefined rather than rejected. The
  // BE's own parser deliberately keeps a frame whose label is unusable
  // (updateMessage.ts, "a missing label must not fail the frame"), and the
  // columns are nullable; a bare `v.optional(v.string())` REJECTS null, and a
  // rejected frame is dropped WHOLE by validateDomainEventFrame. Losing a
  // caseStatusChange over a decorative label would invert DATA-001 severity —
  // and because parseDomainEventFeed still advances the seq cursor, a dropped
  // frame is permanent loss, not a retry. A non-string (e.g. a number) is still
  // rejected: that is real contract drift and should surface.
  caseLabel: v.optional(nullableString),
  organ: v.optional(nullableString),
  occurredAt: parseableTimestamp,
  payload: v.record(v.string(), v.unknown()),
  media: v.optional(v.array(v.unknown())),
  pairKey: v.optional(v.string()),
  spanKey: v.optional(v.string()),
  spanRole: v.optional(v.picklist(['open', 'close'])),
  ts: v.number(),
});

/** Parse a single wire item into a valid frame, or `null`. */
export const validateDomainEventFrame = (
  value: unknown,
): DomainEventFrame | null => {
  const parsed = v.safeParse(domainEventFrameSchema, value);
  return parsed.success ? (parsed.output as DomainEventFrame) : null;
};

/** A finite number off the wire, or undefined. Never NaN — a NaN floor would
 *  poison every comparison it reaches. */
const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Parse a feed envelope; drops malformed events, empty feed on bad envelope. */
export const parseDomainEventFeed = (raw: unknown): DomainEventFeed => {
  if (typeof raw !== 'object' || raw === null) {
    return { events: [], latestSeq: '0', capped: false };
  }
  const { events, latestSeq, historyFloorMs, capped, retentionMs } =
    raw as Record<string, unknown>;
  if (!Array.isArray(events)) {
    return { events: [], latestSeq: '0', capped: false };
  }
  const parsed = events
    .map(validateDomainEventFrame)
    .filter((frame): frame is DomainEventFrame => frame !== null);
  // A gap between the raw and kept counts is a contract-drift signal (the BE
  // wire shape diverging from this schema) — surface it, throttled, with a
  // sample of the first dropped item instead of losing frames silently.
  if (parsed.length < events.length) {
    const droppedSample = events.find(
      item => validateDomainEventFrame(item) === null,
    );
    reportThrottled(
      'domain-event-feed:dropped-frames',
      'domain-event feed: dropped events failing schema validation',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: {
          droppedCount: events.length - parsed.length,
          rawCount: events.length,
          sample: droppedSample,
        },
      },
    );
  }
  return {
    events: parsed,
    latestSeq:
      typeof latestSeq === 'string' && isNumericIdString(latestSeq)
        ? latestSeq
        : '0',
    historyFloorMs: finiteNumber(historyFloorMs),
    capped: capped === true,
    retentionMs: finiteNumber(retentionMs),
  };
};
